/**
 * Переключатель 2FA в профиле (engine/function/profile.php).
 *
 * Клик по лейблу ничего не меняет в базе: totp_post.php на totp_status только
 * отдаёт модалку с QR или с полем кода. totp_status меняется позже, отдельным
 * запросом totp_backup_code_check или totp_status_disable после успешной
 * проверки кода. Браузер же переключает радио сразу по клику, поэтому брошенное
 * на полпути подключение оставляло в профиле «да» при выключенной 2FA.
 *
 * Поэтому клик по переключателю не двигает радио, а состояние применяется по
 * финальному ответу data-totp-done. Список резервных кодов теперь лишь
 * промежуточный шаг: включение завершается проверкой одного из них.
 */

import {translate} from './translations.js';

const SWITCH_SELECTOR = 'input[name="totp_status"]';
const DONE_SELECTOR = '[data-totp-done="1"]';
const BACKUP_CHECK_BUTTON_SELECTOR = 'button[name="totp_backup_code_check"]';
const DISABLE_BUTTON_SELECTOR = 'button[name="totp_status_disable"]';
const RECOVERY_SELECTOR = '[data-totp-recovery]';
const TOTP_ACTION = '/engine/function/totp_post.php';
const FINAL_BUTTONS = ['totp_backup_code_check', 'totp_status_disable'];
let recoveryRequired = false;

const controlOfLabel = (label) =>
    label.control || (label.htmlFor ? document.getElementById(label.htmlFor) : null);

export const switchInputFromTarget = (target) => {
    if (!target || typeof target.closest !== 'function') return null;

    const input = target.closest(SWITCH_SELECTOR);
    if (input) return input;

    const label = target.closest('label');
    if (!label) return null;
    const control = controlOfLabel(label);
    return control && control.matches && control.matches(SWITCH_SELECTOR) ? control : null;
};

const responseHas = (node, selector) =>
    !!node && (node.matches(selector) || !!node.querySelector(selector));

/**
 * @param {Element|null} form форма, чей POST завершился
 * @param {Element|Element[]|null} responseNode вставленные в body корневые узлы ответа
 * @returns {'1'|'0'|null} новое значение totp_status или null, если не подтверждено
 */
export const confirmedStatus = (form, responseNode) => {
    if (!form || !responseNode) return null;
    const nodes = Array.isArray(responseNode) ? responseNode : [responseNode];

    if (form.querySelector(BACKUP_CHECK_BUTTON_SELECTOR)) {
        return nodes.some(node => responseHas(node, DONE_SELECTOR)) ? '1' : null;
    }
    if (form.querySelector(DISABLE_BUTTON_SELECTOR)) {
        return nodes.some(node => responseHas(node, DONE_SELECTOR)) ? '0' : null;
    }
    return null;
};

const applyStatus = (value, root = document) => {
    root.querySelectorAll(SWITCH_SELECTOR).forEach(input => {
        input.checked = input.value === value;
    });
};

//клик обрабатывается до того, как браузер переключит радио, поэтому достаточно
//отменить действие по умолчанию. js_form.js отправляет противоположное текущему
//значение независимо от того, на какую половину тумблера нажал пользователь
const handleClick = (e) => {
    if (e.defaultPrevented) return;
    if (switchInputFromTarget(e.target)) e.preventDefault();
};

const handleFormResponse = (e) => {
    if (recoveryRequired) return;
    const status = confirmedStatus(e.target, e.detail?.responseNodes || e.detail?.responseNode);
    if (status !== null) applyStatus(status);
};

const handleRequestFailure = (e) => {
    const {action, buttonName, form} = e.detail || {};
    if (action !== TOTP_ACTION || !FINAL_BUTTONS.includes(buttonName)) return;
    recoveryRequired = true;
    form?.remove();

    const switches = [...document.querySelectorAll(SWITCH_SELECTOR)];
    switches.forEach(input => {
        input.disabled = true;
        input.setAttribute('aria-describedby', 'totp-recovery-message');
        [...input.labels].forEach(label => label.setAttribute('aria-disabled', 'true'));
    });
    showRecovery();
};

const showRecovery = () => {
    const existing = document.querySelector(RECOVERY_SELECTOR);
    if (existing?.classList.contains('open') && existing.classList.contains('show')) {
        document.body.append(existing);
        existing.querySelector('#totp-recovery-message').focus();
        return;
    }
    // Replace a closing modal so its pending animation timers cannot hide the new one.
    existing?.remove();
    const recovery = document.createElement('div');
    recovery.dataset.totpRecovery = '1';
    recovery.className = 'modal item-close block open show';
    recovery.setAttribute('role', 'alertdialog');
    recovery.setAttribute('aria-modal', 'true');
    recovery.setAttribute('aria-labelledby', 'totp-recovery-title');
    recovery.setAttribute('aria-describedby', 'totp-recovery-message');
    recovery.innerHTML = '<div class="modal-wrapper"><div class="modal-body">' +
        '<div class="block__item modal-form modal-inner"><div class="block__item-top">' +
        '<div class="block__item-title modal-title" id="totp-recovery-title"></div>' +
        '<button type="button" class="block__item-link js-close close-icon max-content"></button>' +
        '</div><div class="block__item-wrap"></div></div></div></div>';
    recovery.querySelector('#totp-recovery-title').textContent = translate('Error');
    recovery.querySelector('.js-close').setAttribute('aria-label', translate('Close'));
    const message = document.createElement('p');
    message.id = 'totp-recovery-message';
    message.tabIndex = -1;
    message.textContent = translate('Unable to confirm the result. Refresh the page to check your 2FA status.');
    const refresh = document.createElement('button');
    refresh.type = 'button';
    refresh.className = 'btn btn-transparent max-content';
    refresh.textContent = translate('Refresh page');
    refresh.addEventListener('click', () => window.location.reload());
    recovery.querySelector('.block__item-wrap').append(message, refresh);
    document.body.append(recovery);
    message.focus();
};

const guardUnknownState = (e) => {
    if (!recoveryRequired) return;
    const button = e.target.closest?.(`${BACKUP_CHECK_BUTTON_SELECTOR}, ${DISABLE_BUTTON_SELECTOR}`);
    const isFinalRequest = button?.closest('.form_for_js_in_div')?.getAttribute('data-action') === TOTP_ACTION;
    if (switchInputFromTarget(e.target) || isFinalRequest) {
        // Labels are submitted by body's delegated jQuery handler, even for disabled radios.
        e.preventDefault();
        e.stopImmediatePropagation();
        showRecovery();
    }
};

export const totpSwitch = () => {
    document.addEventListener('click', guardUnknownState, true);
    document.addEventListener('click', handleClick);
    document.addEventListener('js-form:response', handleFormResponse);
    document.addEventListener('js-form:request-failed', handleRequestFailure);
};
